/**
 * @file    DrawerFooter.tsx
 * @scope   VIEW
 * @summary Action bar at the bottom of the drawer; the pair of buttons depends on the active tab.
 * @consumes DiyDrawerMainTab, FieldsManagerTab (tab identity only)
 * @produces JSX only
 * @sideEffects None — all handlers are injected.
 * @notes Only one of the two bars is rendered at a time; any other tab renders nothing.
 */
import React from 'react';
import { FieldsManagerTab } from 'components/DiyWidgetEngine/Subtypes/Enterprise/Views/FieldsManager/FieldsManager';
import { DiyDrawerMainTab } from '../../common/constants/tabs';

export interface DrawerFooterProps {
  currentTab: { name: string };
  /** Main view: discards the drawer without deleting a previously saved widget. */
  onClose: () => void;
  /** Main view: commits the widget. */
  onSave: (event: any) => void;
  /** Main view: true while the widget is not savable. */
  saveDisabled: boolean;
  /** FieldsManager view: cancels the whole drawer. */
  onFieldsManagerCancel: () => void;
  /** FieldsManager view: commits the field selection. */
  onFieldsManagerSave: (event: any) => void;
  /** FieldsManager view: true while the field selection is incomplete. */
  fieldsManagerSaveDisabled: boolean;
}

const DrawerFooter: React.FC<DrawerFooterProps> = ({
  currentTab,
  onClose,
  onSave,
  saveDisabled,
  onFieldsManagerCancel,
  onFieldsManagerSave,
  fieldsManagerSaveDisabled,
}) => (
  <>
    {/* Submitting buttons - only visible in main view */}
    {currentTab.name === DiyDrawerMainTab.name && (
      <div className="diy-drawer-controls">
        <button className="diy-button diy-button-enabled-inverse" onClick={onClose}>
          Close
        </button>
        <button className="diy-button diy-button-enabled" onClick={onSave} disabled={saveDisabled}>
          Save
        </button>
      </div>
    )}
    {currentTab.name === FieldsManagerTab.name && (
      <div className="diy-drawer-controls">
        <button className="diy-button diy-button-enabled-inverse" onClick={onFieldsManagerCancel}>
          Cancel
        </button>
        <button
          className="diy-button diy-button-enabled"
          onClick={onFieldsManagerSave}
          disabled={fieldsManagerSaveDisabled}
        >
          Save
        </button>
      </div>
    )}
  </>
);

export default React.memo(DrawerFooter);
